/**
 * CollisionBadge / CollisionPanel — Affichage des collisions doctrinales
 * détectées pendant le mode Collision (Annuaire, Ontologie).
 *
 * Un badge apparaît sur chaque entité impliquée dans une collision non
 * banale (kind = "opportunity" | "risk"). Le badge affiche le score en
 * pointillé — invitation discrète à explorer.
 *
 * Cliquer sur une entité en collision ouvre le CollisionPanel : titre,
 * lecture stratégique, action recommandée, score de confiance, hypothèses.
 */
import { Sparkles, AlertTriangle, ArrowRight, X, Search, CheckCircle2 } from "lucide-react";
import ConfidenceBadge from "@/components/ConfidenceBadge";

export function CollisionBadge({ collisions }) {
  if (!collisions || collisions.length === 0) return null;
  // On affiche la collision la plus forte
  const best = [...collisions].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
  const isOpp = best.kind === "opportunity";
  const color = isOpp ? "text-[#1D9E75] border-[#1D9E75]" : "text-[#E24B4A] border-[#E24B4A]";
  const Icon = isOpp ? Sparkles : AlertTriangle;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border-dashed border ${color} bg-[#0A0A0F]/70 tabular-nums`}
      data-testid="collision-badge"
      title={best.title}
    >
      <Icon className="w-3 h-3" />
      {best.score}
    </span>
  );
}

export function CollisionPanel({ collision, onClose, onConvertOpportunity, onConvertRisk }) {
  if (!collision) return null;
  const isOpp = collision.kind === "opportunity";
  const color = isOpp ? "#1D9E75" : "#E24B4A";
  const Icon = isOpp ? Sparkles : AlertTriangle;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="collision-panel-overlay"
    >
      <div
        className="w-full max-w-lg rounded-lg border border-white/10 bg-[#0A0A0F] p-5 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color }}>
              <Icon className="w-3 h-3 inline mr-1" />
              {isOpp ? "Opportunité systémique détectée" : "Risque systémique détecté"}
            </div>
            <div className="text-base text-white mt-1 font-semibold">{collision.title}</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Collision entre <span className="text-white">{collision.entity_a?.name}</span> et <span className="text-white">{collision.entity_b?.name}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white flex-shrink-0" data-testid="collision-panel-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded border-dashed border tabular-nums"
            style={{ color, borderColor: color }}
          >
            SCORE {collision.score}
          </span>
          <ConfidenceBadge
            value={collision.confidence}
            rationale={collision.reading}
            assumptions={collision.assumptions || []}
            caveats={collision.caveats || []}
            compact
          />
        </div>

        {collision.fact && (
          <div className="pt-3 border-t border-white/5" data-testid="collision-fact-block">
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-1.5 font-semibold inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Fait vérifiable
            </div>
            <p className="text-[12px] text-zinc-100 leading-snug italic">« {collision.fact} »</p>
          </div>
        )}

        {collision.reading && (
          <div className="pt-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold">Lecture stratégique</div>
            <p className="text-[12px] text-zinc-200 leading-snug">{collision.reading}</p>
          </div>
        )}

        {collision.action_hint && (
          <div className="pt-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color }}>Action à envisager</div>
            <p className="text-[12px] text-zinc-200 leading-snug">{collision.action_hint}</p>
          </div>
        )}

        {Array.isArray(collision.sources_hint) && collision.sources_hint.length > 0 && (
          <div className="pt-3 border-t border-white/5" data-testid="collision-sources-block">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold inline-flex items-center gap-1.5">
              <Search className="w-3 h-3" /> Vérifier soi-même
            </div>
            <ul className="space-y-1">
              {collision.sources_hint.map((q, i) => (
                <li key={i}>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(q)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-[#378ADD] hover:underline inline-flex items-center gap-1"
                    data-testid={`collision-source-link-${i}`}
                  >
                    <Search className="w-2.5 h-2.5" /> « {q} »
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
          {isOpp && onConvertOpportunity && (
            <button
              onClick={() => onConvertOpportunity(collision)}
              className="text-[11px] px-3 py-1.5 rounded bg-[#1D9E75] text-black font-semibold hover:brightness-110 inline-flex items-center gap-1"
              data-testid="collision-convert-opp"
            >
              Ajouter aux opportunités <ArrowRight className="w-3 h-3" />
            </button>
          )}
          {!isOpp && onConvertRisk && (
            <button
              onClick={() => onConvertRisk(collision)}
              className="text-[11px] px-3 py-1.5 rounded bg-[#E24B4A] text-white font-semibold hover:brightness-110 inline-flex items-center gap-1"
              data-testid="collision-convert-risk"
            >
              Ajouter aux risques <ArrowRight className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={onClose}
            className="text-[11px] px-3 py-1.5 rounded border border-white/15 text-zinc-300 hover:bg-white/5"
            data-testid="collision-panel-dismiss"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
